import {ReactElement, RefAttributes, forwardRef, ForwardedRef, ReactNode} from 'react';
import {Paginated, SelectOption} from '@chekin/core';
import {SearchableSelect, SearchableSelectProps} from '@chekinapp/ui';
import {useSearchableSelectQuery} from 'hooks';

type AirbnbPaginatedSearchableSelectProps<
  T = undefined,
  V extends string | number = string,
  L extends ReactNode = string,
> = Omit<
  SearchableSelectProps<T, V, L>,
  | 'options'
  | 'searchValue'
  | 'onSearchChange'
  | 'loading'
  | 'hasNextPage'
  | 'onLoadMore'
  | 'filterOption'
> & {
  queryKey: readonly unknown[];
  queryFn: (params: {
    page: number;
    searchQuery: string;
  }) => Promise<Paginated<SelectOption<T, V, L>>>;
  resetKey?: unknown;
  minSearchQueryLength?: number;
  emptyMessage?: string;
  searchHintMessage?: string;
};

const PaginatedSearchableSelectInternal = <
  T = undefined,
  V extends string | number = string,
  L extends ReactNode = string,
>(
  {
    queryKey,
    queryFn,
    resetKey,
    minSearchQueryLength = 0,
    emptyMessage,
    searchHintMessage,
    value,
    ...props
  }: AirbnbPaginatedSearchableSelectProps<T, V, L>,
  ref: ForwardedRef<HTMLButtonElement>,
) => {
  const {
    options,
    searchValue,
    setSearchValue,
    onOpenChange,
    loading,
    hasNextPage,
    onLoadMore,
    canSearch,
  } = useSearchableSelectQuery({
    queryKey,
    queryFn,
    value,
    resetKey,
    minSearchQueryLength,
  });

  return (
    <SearchableSelect
      ref={ref}
      {...props}
      value={value}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onOpenChange={onOpenChange}
      loading={loading}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      filterOption={null}
      noOptionsMessage={() => {
        if (!canSearch) {
          return (
            searchHintMessage ??
            `Type at least ${minSearchQueryLength} characters to search`
          );
        }

        return emptyMessage ?? props.noOptionsMessage?.();
      }}
    />
  );
};

type PaginatedSearchableSelectComponent = <
  T = undefined,
  V extends string | number = string,
  L extends ReactNode = string,
>(
  props: AirbnbPaginatedSearchableSelectProps<T, V, L> & RefAttributes<HTMLButtonElement>,
) => ReactElement | null;

const PaginatedSearchableSelect = forwardRef(
  PaginatedSearchableSelectInternal,
) as PaginatedSearchableSelectComponent;

export {PaginatedSearchableSelect};
export type {AirbnbPaginatedSearchableSelectProps};
